import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import NavBar from "../components/NavBar";
import { RefPrice } from "../lib/price";
import { MultiSelect } from "../components/MultiSelect";
import { CatalogMap } from "../components/CatalogMap";
import { MultiRequestDialog } from "../components/MultiRequestDialog";
import { Search, MapPin, Users, Sparkles, ArrowRight, List, Map as MapIcon, Check, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEL_KEY = "matchappart-selection";
const sel = "px-3 py-2.5 bg-[#F5F3ED] border border-[#E5E1D8] rounded-md text-sm focus:outline-none focus:border-[#8C6C50]";

function useSelection() {
  const [selected, setSelected] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(SEL_KEY)) || {}; } catch { return {}; }
  });
  useEffect(() => { sessionStorage.setItem(SEL_KEY, JSON.stringify(selected)); }, [selected]);
  const toggle = (l) => setSelected((s) => {
    const n = { ...s };
    if (n[l.id]) delete n[l.id];
    else n[l.id] = { id: l.id, title: l.title, city: l.city, min_duration_months: l.min_duration_months,
      price_1m_month: l.price_1m_month, price_1m_night: l.price_1m_night, price_3m_month: l.price_3m_month, price_3m_night: l.price_3m_night };
    return n;
  });
  return [selected, toggle, () => setSelected({})];
}

export default function Catalog() {
  const [listings, setListings] = useState([]);
  const [cities, setCities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, toggle, clearSel] = useSelection();
  const [filters, setFilters] = useState({ city: [], department: [], rtype: [], apt: [], guests: "", max_price: "", budget_unit: "month" });
  const count = Object.keys(selected).length;

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (Array.isArray(v)) { if (v.length) params[k] = v.join(","); }
        else if (v && !(k === "budget_unit" && !filters.max_price)) params[k] = v;
      });
      const [{ data: list }, { data: ds }] = await Promise.all([
        axios.get(`${API}/listings`, { params, withCredentials: true }),
        axios.get(`${API}/listings-meta/departments`, { withCredentials: true }),
      ]);
      setListings(list);
      setDepartments(ds);
    } catch (e) {
      toast.error("Impossible de charger le catalogue");
    } finally {
      setLoading(false);
    }
  };

  const depKey = filters.department.join(",");
  useEffect(() => {
    const params = depKey ? { department: depKey } : {};
    axios.get(`${API}/listings-meta/cities`, { params, withCredentials: true })
      .then(({ data }) => {
        setCities(data);
        setFilters((f) => ({ ...f, city: f.city.filter((c) => data.includes(c)) }));
      })
      .catch(() => setCities([]));
  }, [depKey]);

  const requestLink = `/demande?${new URLSearchParams(
    Object.fromEntries([["city", filters.city[0]], ["guests", filters.guests], ["budget", filters.budget_unit === "month" ? filters.max_price : ""]].filter(([, x]) => x))
  )}`;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[#F5F3ED]">
      <NavBar />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-10">
        <div className="text-xs tracking-[0.25em] uppercase font-semibold text-[#8C6C50]">Catalogue privé</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-4 text-[#1A1A1A] leading-[1.05]">
          Trouvez votre hébergement<br />pour quelques mois.
        </h1>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-6">
        <div className="bg-white border border-[#E5E1D8] p-6 rounded-md grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="catalog-filters">
          <MultiSelect
            placeholder="Tous départements"
            testid="filter-department"
            value={filters.department}
            onChange={(v) => setFilters((f) => ({ ...f, department: v }))}
            groups={[{ key: "idf", label: "Île-de-France", values: departments.filter((d) => d.idf).map((d) => d.code) }]}
            options={departments.map((d) => ({ value: d.code, label: d.label, short: d.code, indent: d.idf }))}
          />
          <MultiSelect
            placeholder={filters.department.length ? "Toutes villes sélectionnées" : "Toutes villes"}
            testid="filter-city"
            value={filters.city}
            onChange={(v) => setFilters((f) => ({ ...f, city: v }))}
            options={cities.map((c) => ({ value: c, label: c }))}
          />
          <MultiSelect
            placeholder="Tous types de résidence"
            testid="filter-rtype"
            value={filters.rtype}
            onChange={(v) => setFilters((f) => ({ ...f, rtype: v }))}
            options={[{ value: "aparthotel", label: "Appart'hôtel" }, { value: "student", label: "Résidence étudiante" }, { value: "senior", label: "Résidence senior" }]}
          />
          <MultiSelect
            placeholder="Tous types d'appartements"
            testid="filter-apt"
            value={filters.apt}
            onChange={(v) => setFilters((f) => ({ ...f, apt: v }))}
            options={[{ value: "studio", label: "Studio" }, { value: "t2", label: "2 pièces" }, { value: "t3", label: "3 pièces" }]}
          />
          <input
            type="number"
            min="1"
            placeholder="Personnes"
            value={filters.guests}
            onChange={(e) => setFilters({ ...filters, guests: e.target.value })}
            className="px-3 py-2.5 bg-[#F5F3ED] border border-[#E5E1D8] rounded-md text-sm focus:outline-none focus:border-[#8C6C50]"
            data-testid="filter-guests"
          />
          <div className="flex">
            <input
              type="number"
              min="0"
              placeholder="Budget max (€)"
              value={filters.max_price}
              onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
              className="min-w-0 flex-1 px-3 py-2.5 bg-[#F5F3ED] border border-[#E5E1D8] rounded-l-md text-sm focus:outline-none focus:border-[#8C6C50]"
              data-testid="filter-max-price"
            />
            <select
              value={filters.budget_unit}
              onChange={(e) => setFilters({ ...filters, budget_unit: e.target.value })}
              className="px-2 py-2.5 bg-white border border-l-0 border-[#E5E1D8] rounded-r-md text-sm focus:outline-none focus:border-[#8C6C50]"
              data-testid="filter-budget-unit"
            >
              <option value="month">/ mois</option>
              <option value="night">/ nuit</option>
            </select>
          </div>
          <button
            onClick={load}
            className="btn-primary rounded-md py-2.5 text-sm font-medium flex items-center justify-center gap-2"
            data-testid="filter-apply-btn"
          >
            <Search className="w-4 h-4" /> Rechercher
          </button>
          <div className="inline-flex self-center justify-self-start rounded-full bg-[#8C6C50] p-1" data-testid="catalog-view-toggle">
            {[["list", "Liste", List], ["map", "Carte", MapIcon]].map(([k, label, Icon]) => (
              <button key={k} type="button" onClick={() => setView(k)} className={`px-4 py-1.5 rounded-full text-sm inline-flex items-center gap-1.5 transition-colors ${view === k ? "bg-white text-[#8C6C50] font-medium" : "text-white/85 hover:text-white hover:bg-[#6B5B49]"}`} data-testid={`catalog-view-${k}`}>
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10 pb-32">
        {!loading && listings.length > 0 && (
          <div className="mb-6">
            <p className="text-sm text-[#666]" data-testid="catalog-count">{listings.length} annonce{listings.length > 1 ? "s" : ""} · cochez-en plusieurs pour une demande groupée</p>
          </div>
        )}
        {loading && <div className="text-sm text-[#666]">Chargement…</div>}
        {!loading && listings.length === 0 && (
          <div className="bg-white border border-[#E5E1D8] rounded-md p-8 sm:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6" data-testid="catalog-empty">
            <div>
              <div className="font-serif text-2xl text-[#1A1A1A]">Aucune annonce ne correspond à vos critères.</div>
              <p className="text-sm text-[#666] mt-2">Décrivez votre besoin : nous cherchons pour vous auprès de nos résidences partenaires.</p>
            </div>
            <Link to={requestLink} className="btn-primary px-6 py-3 rounded-full text-sm font-medium inline-flex items-center gap-2 shrink-0" data-testid="catalog-empty-request-btn">
              Faire une demande sur mesure <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
        {view === "map" && !loading && listings.length > 0 && <CatalogMap listings={listings} selected={selected} toggle={toggle} />}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 ${view === "map" ? "hidden" : ""}`} data-testid="catalog-grid">
          {listings.map((l) => (
            <Link key={l.id} to={`/listings/${l.id}`} className="group card-hover block" data-testid={`listing-card-${l.id}`}>
              <div className={`aspect-[4/3] overflow-hidden mb-5 bg-[#EFECE5] relative ${selected[l.id] ? "ring-2 ring-[#2B3029] ring-offset-2 ring-offset-[#F5F3ED]" : ""}`}>
                {l.image_urls?.[0] && (
                  <img src={l.image_urls[0]} alt={l.title} className="w-full h-full object-cover" />
                )}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(l); }}
                  aria-pressed={!!selected[l.id]}
                  aria-label="Sélectionner cette annonce"
                  className={`absolute top-3 left-3 w-8 h-8 rounded-md border-2 flex items-center justify-center shadow transition-colors ${selected[l.id] ? "bg-[#2B3029] border-[#2B3029] text-white" : "bg-white/90 border-white text-transparent hover:text-[#8C6C50]"}`}
                  data-testid={`listing-select-${l.id}`}
                >
                  <Check className="w-5 h-5" strokeWidth={3} />
                </button>
              </div>
              {l.brand && <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[#8C6C50] mb-1.5" data-testid={`listing-card-brand-${l.id}`}>{l.brand}</div>}
              <h3 className="font-serif text-xl sm:text-2xl text-[#1A1A1A] group-hover:text-[#8C6C50] transition-colors">
                {l.title}
              </h3>
              {l.residence_name && <div className="mt-1 text-sm text-[#2B3029]" data-testid={`listing-card-residence-${l.id}`}>{l.residence_name}</div>}
              <div className="mt-3 flex items-center justify-between text-sm text-[#666]">
                <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {[l.postal_code, l.city].filter(Boolean).join(" ")}</span>
                <span className="flex items-center gap-1.5"><Users className="w-4 h-4" /> {l.capacity} pers.</span>
              </div>
              <div className="mt-4 pt-4 border-t border-[#E5E1D8] flex items-baseline justify-between">
                <RefPrice l={l} testid={`listing-card-price-${l.id}`} />
                <span className="text-xs text-[#666]">dès {l.min_duration_months} mois</span>
              </div>
            </Link>
          ))}
        </div>
        {!loading && listings.length > 0 && (
          <div className="mt-16 border-t border-[#E5E1D8] pt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="catalog-request-banner">
            <p className="text-sm text-[#666] flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#8C6C50]" /> Vous ne trouvez pas votre bonheur ? Nous cherchons pour vous.</p>
            <Link to={requestLink} className="text-sm font-medium text-[#8C6C50] underline underline-offset-4" data-testid="catalog-request-link">Faire une demande sur mesure</Link>
          </div>
        )}
      </section>

      {count > 0 && (
        <div className="fixed bottom-5 inset-x-0 z-40 px-4 flex justify-center" data-testid="selection-bar">
          <div className="bg-[#2B3029] text-white rounded-full shadow-xl pl-6 pr-2 py-2 flex items-center gap-4">
            <span className="text-sm whitespace-nowrap" data-testid="selection-count">{count} sélectionnée{count > 1 ? "s" : ""}</span>
            <button onClick={clearSel} className="text-xs text-white/70 hover:text-white underline underline-offset-2" data-testid="selection-clear">Vider</button>
            <button onClick={() => setDialogOpen(true)} className="bg-[#8C6C50] hover:bg-[#A07D5F] transition-colors rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2" data-testid="selection-request-btn">
              <Send className="w-4 h-4" /> Envoyer une demande
            </button>
          </div>
        </div>
      )}
      <MultiRequestDialog open={dialogOpen} onOpenChange={setDialogOpen} selected={selected} toggle={toggle} onSent={() => { setDialogOpen(false); clearSel(); }} />
    </div>
  );
}
